import { useState, useCallback, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { Contact } from '../types'; // Adjust path
import { useCRMTagsContext } from '../CRMTagsContext'; // Adjust path
import { useContactAttributesConfig } from './useContactAttributesConfig';
import { FieldType } from '../../../hooks/useSchema';
import { useCRMContext } from '../CRMContext';

export const EXPORTABLE_FIELDS = [
  { value: '_id', label: 'ID' },
  { value: 'first_name', label: 'Имя' },
  { value: 'last_name', label: 'Фамилия' },
  { value: 'middle_name', label: 'Отчество' },
  { value: 'gender', label: 'Пол' },
  { value: 'birth_date', label: 'Дата рождения' },
  { value: 'geo_country', label: 'Страны' },
  { value: 'geo_city', label: 'Города' },
  { value: 'phones', label: 'Телефоны' },
  { value: 'emails', label: 'Email' },
  { value: 'description', label: 'Описание' },
  { value: 'organization_name', label: 'Компания' },
  { value: 'organization_position', label: 'Должность' },
  { value: 'tags', label: 'Теги' },
  { value: 'categories', label: 'Категории' },
  // New Fields
  { value: 'consent_pd', label: 'Согласие на ПД' },
  { value: 'consent_emails', label: 'Согласие на рассылки' },
  { value: 'communication_languages', label: 'Язык коммуникации' },
];

export const useExportContacts = () => {
  const { tagsMap, } = useCRMTagsContext();
  const { attributes: customAttributes, schema: customSchema } = useContactAttributesConfig();
  const ctx = useCRMContext();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [contactsToExport, setContactsToExport] = useState<Contact[]>([]);
  
  // Dynamically merge base fields with custom attributes
  const exportableFields = useMemo(() => {
    const customFields = customAttributes.map(attr => ({
      value: attr.code,
      label: `${attr.label} (Доп.)` // Marking them visually
    }));
    return [...EXPORTABLE_FIELDS, ...customFields];
  }, [customAttributes]);

  // Settings
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [exportAllMulti, setExportAllMulti] = useState<boolean>(false); // true = all comma separated, false = first only

  const open = useCallback((contacts: Contact[]) => {
    setContactsToExport(contacts);
    // Auto-select all fields (including current custom fields) when opening
    setSelectedFields(exportableFields.map(f => f.value));
    setIsOpen(true);
  }, [exportableFields]);

  const close = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setContactsToExport([]), 200); // Clear after transition
  }, []);

  const toggleField = useCallback((fieldValue: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldValue) 
        ? prev.filter(f => f !== fieldValue) 
        : [...prev, fieldValue]
    );
  }, []);

  const doExport = useCallback(async () => {
    if (contactsToExport.length === 0 || selectedFields.length === 0) return;
    
    setIsExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Контакты');

      // 1. Add Headers
      const orderedFields = exportableFields.filter(f => selectedFields.includes(f.value));
      const headerRow = worksheet.addRow(orderedFields.map(f => f.label));
      
      // Styling the header
      headerRow.font = { bold: true };
      headerRow.eachCell(cell => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F0F0' }
        };
      });

      // 2. Add Data
      contactsToExport.forEach(contact => {
        const rowData = orderedFields.map(field => {
          
          // --- 0. Handle Custom Attributes ---
          const customConfig = customSchema[field.value];
          if (customConfig) {
            // Check in nested attributes object first, fallback to root level
            const val = contact.attributes?.[field.value] ?? (contact as any)[field.value];
            
            if (val === undefined || val === null) return '';
            
            // Format booleans to user-friendly text for Excel
            if (customConfig.type === FieldType.bool) {
              return val ? 'yes' : 'no';
            }
            if(customConfig.type === FieldType.select) {
              const attr = customAttributes.find(a => a.code === field.value);
              const label = attr?.select_options?.find(o => o.value === val)?.label;
              return label || val;
            }
            return String(val);
          }

          // --- Handle Tags ---
          if (field.value === 'tags') {
            if (!contact.tags || contact.tags.length === 0) return '';
            return contact.tags
              .map(tagId => tagsMap[tagId.toString()]?.name || `[ID: ${tagId}]`)
              .join(', ');
          }

          if (field.value === 'categories') {
            if (!contact.categories || contact.categories.length === 0) return '';
            return contact.categories
              .map(categoryCode => ctx.settings.contact_categories?.find(c => c.code === categoryCode)?.label || categoryCode)
              .join(', ');
          }

          // --- Handle Geo & Languages (Arrays of Strings) ---
          if (field.value === 'geo_country' || field.value === 'geo_city' || field.value === 'communication_languages') {
            const arrItems = (contact as any)[field.value];
            if (!arrItems || !Array.isArray(arrItems) || arrItems.length === 0) return '';
            return arrItems.join(', ');
          }
          
          // --- Handle Emails and Phones ---
          if (field.value === 'emails' || field.value === 'phones') {
            const items = (contact as any)[field.value] || [];
            if (items.length === 0) return '';
            
            if (exportAllMulti) {
              return items.map((i: any) => i.value).join(', ');
            } else {
              const itemsX = items || [];
              const main = itemsX.filter((x: any) => x.is_main);
              const final = main.length ? main : itemsX;
              return final[0]?.value || '';
            }
          }
          
          // --- Handle standard fields ---
          const val = (contact as any)[field.value];
          return val !== undefined && val !== null ? String(val) : '';
        });

        worksheet.addRow(rowData);
      });

      // Adjust column widths automatically
      worksheet.columns.forEach(column => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, cell => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) maxLength = columnLength + 2;
        });
        column.width = Math.min(maxLength, 50); // Cap width to 50
      });

      // 3. Generate file and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `Контакты_Экспорт_${dateStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      close();
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsToExport, selectedFields, exportAllMulti, tagsMap, close, exportableFields, customSchema, ctx.settings.contact_categories]);

  return {
    isOpen,
    isExporting,
    exportableFields,
    selectedFields,
    exportAllMulti,
    contactsToExport,
    open,
    close,
    toggleField,
    setExportAllMulti,
    doExport
  };
};

export type ExportContactsData = ReturnType<typeof useExportContacts>;